import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { useApp } from "../context/AppContext";
import { waUrl, CONTACT_TEXT } from "../waHelper";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" {...props}>
    <path d="M16.5 3c.3 2 1.5 3.6 3.5 3.9v2.7c-1.3.1-2.5-.2-3.6-.9v6.2c0 3.2-2.5 5.6-5.6 5.6a5.6 5.6 0 1 1 1.5-11v2.9c-.4-.1-.8-.2-1.2-.2a2.7 2.7 0 1 0 2.7 2.7V3h2.7z"/>
  </svg>
);

export default function Footer() {
  const { settings, t } = useApp();
  const socials = [
    { key: "facebook", icon: Facebook, url: settings.facebook },
    { key: "instagram", icon: Instagram, url: settings.instagram },
    { key: "tiktok", icon: TikTokIcon, url: settings.tiktok },
    { key: "youtube", icon: Youtube, url: settings.youtube },
  ].filter((s) => s.url);

  return (
    <footer className="border-t border-black/10 bg-neutral-100">
      <div className="max-w-7xl mx-auto px-5 py-14 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-1">
          <div className="flex items-center mb-4">
            <img src="/smartlogistics-full-logo.png" alt="SmartLogistics" className="h-10 w-auto object-contain" />
          </div>
          <p className="text-sm text-neutral-600 leading-relaxed">
            {t("footer.tagline")}
          </p>
          <div className="flex gap-2 mt-5">
            {socials.length ? socials.map((s) => (
              <a key={s.key} href={s.url} target="_blank" rel="noreferrer"
                className="w-9 h-9 rounded-lg glass grid place-items-center text-neutral-700 hover:text-orange-500 hover:border-orange-500/50 transition-colors">
                <s.icon className="w-4 h-4" />
              </a>
            )) : <span className="text-xs text-neutral-300">Social links coming soon</span>}
          </div>
        </div>

        <div>
          <h4 className="font-display font-semibold mb-4 text-sm uppercase tracking-wider text-neutral-700">{t("footer.company")}</h4>
          <ul className="space-y-2.5 text-sm text-neutral-600">
            <li><Link to="/about" className="hover:text-orange-500">{t("footer.about")}</Link></li>
            <li><Link to="/services" className="hover:text-orange-500">{t("footer.services")}</Link></li>
            <li><Link to="/coverage" className="hover:text-orange-500">{t("footer.coverage")}</Link></li>
            <li><Link to="/support" className="hover:text-orange-500">{t("footer.support")}</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-semibold mb-4 text-sm uppercase tracking-wider text-neutral-700">{t("footer.getStarted")}</h4>
          <ul className="space-y-2.5 text-sm text-neutral-600">
            <li><Link to="/send" className="hover:text-orange-500">{t("footer.send")}</Link></li>
            <li><Link data-testid="footer-inquiry-link" to="/inquiry" className="hover:text-orange-500">{t("inquiry.nav")}</Link></li>
            <li><Link to="/signup" className="hover:text-orange-500">{t("footer.createAccount")}</Link></li>
            <li><Link to="/support" className="hover:text-orange-500">{t("footer.review")}</Link></li>
            <li><Link to="/support" className="hover:text-orange-500">{t("footer.faqs")}</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-semibold mb-4 text-sm uppercase tracking-wider text-neutral-700">{t("footer.contact")}</h4>
          <ul className="space-y-3 text-sm text-neutral-600">
            <li data-testid="footer-support-number" className="flex items-center gap-2.5"><Phone className="w-4 h-4 text-orange-500" /> +{settings.whatsapp}</li>
            <li className="flex items-center gap-2.5"><Mail className="w-4 h-4 text-orange-500" /> {settings.email}</li>
            <li className="flex items-start gap-2.5"><MapPin className="w-4 h-4 text-orange-500 mt-0.5" /> {settings.address}</li>
            <li>
              <a data-testid="footer-whatsapp-link" href={waUrl(settings.whatsapp, CONTACT_TEXT)} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-400 font-medium">
                <MessageCircle className="w-4 h-4" /> {t("footer.chat")}
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-black/10 py-5 text-center text-xs text-neutral-500">
        © {new Date().getFullYear()} SmartLogistics. {t("footer.rights")}
      </div>
    </footer>
  );
}
